import { useCallback } from 'react'
import { clearOrderInProgress, saveOrder, setOrderInProgress } from '../../actions/order'
import { useDispatch, useSelector } from 'react-redux'
import { getOrder, getOrderInProgress, getSelectedOrder } from '../../selectors/order'
import { clearCart, setCartFromTab } from '../../actions/cart'
import { printOrder } from '../../actions/printer'
import { setRoute } from '../../VNRoutes/ActionCreators'
import { clearPromotionsOnOrder } from '../../actions/promotion'
import { PRINT_TYPES } from '../../utils/printing'
import { setConnectionRequiredDialog, setPrinterUnavailableOfflineDialog } from '../../VNDialogs/ActionCreators'
import { getCloudPrintingEnabled } from '../../selectors/printer'
import { getNetworkAvailable } from '../../selectors/config'
import { getActiveAttendantId } from '../../selectors/attendant'
import { ToastManager } from '../../utils/toastManager'
import { createOrder } from '../Apis'

/**
 * Hook for handling printer actions
 *
 * @returns
 */
const usePrinter = (orderUuid = null) => {
  const dispatch = useDispatch()
  const order = useSelector(getSelectedOrder)
  const orderById = useSelector((state) => getOrder(state, orderUuid))
  const orderInProgress = useSelector(getOrderInProgress)
  const cloudPrintingEnabled = useSelector(getCloudPrintingEnabled)
  const networkConnected = useSelector(getNetworkAvailable)
  const employeeId = useSelector(getActiveAttendantId)

  /**
   * When dealing with cloud printing:
   *   - stops request if offline
   *   - appends employeeId to the order
   *   - syncs the order if it was not created in stadium yet
   *   - saves the order if successful
   */
  const syncOrderIfNeeded = useCallback(
    async (curOrder, type = null, displayPrinterUnavailableOfflineDialog = true) => {
      if (!networkConnected && cloudPrintingEnabled) {
        if (displayPrinterUnavailableOfflineDialog) {
          dispatch(setPrinterUnavailableOfflineDialog(true, type))
        }
        return false
      }

      if (!curOrder?.wasCreatedInStadium && cloudPrintingEnabled) {
        const payload = { ...curOrder, employeeId }
        const result = await createOrder(payload)
        if (!result) {
          ToastManager.error('Failed to sync order')
          return false
        }
        dispatch(saveOrder({ ...payload, wasCreatedInStadium: true }))
      }

      return true
    },
    [networkConnected, cloudPrintingEnabled, dispatch, employeeId],
  )

  /**
   * On "Print Check", prints the order as is anbd redirects to the orders page
   */
  const printCheckHandler = useCallback(async () => {
    if (!(await syncOrderIfNeeded(order, 'check'))) return

    dispatch(
      printOrder({
        orderUuid: order?.uuid,
        redirectionRoute: '/orders',
        // TODO: [T38964] remove this when we have a proper 'check' printing
        // printType: PRINT_TYPES.CHECK,
      }),
    )
    dispatch(clearOrderInProgress())
  }, [syncOrderIfNeeded, order, dispatch])

  /**
   * On "Print Slip", sets the order in progress and redirects to the Tender Selection page
   */
  const printSlipHandler = useCallback(async () => {
    // we were online and are trying to modify order while offline
    if (!networkConnected && order?.wasCreatedInStadium) {
      dispatch(setConnectionRequiredDialog(true))
      return
    }

    // don't rely on saga to sync before changing payments
    await syncOrderIfNeeded(order, 'check', false)

    // check if order has payment attached already
    dispatch(setOrderInProgress(order))
    dispatch(setCartFromTab(order))

    dispatch(setRoute('/tender-selection'))
  }, [dispatch, networkConnected, order, syncOrderIfNeeded])

  /**
   * On "Reprint Slip", reprints the order slip and redirects to the orders page
   */
  const reprintSlipHandler = useCallback(async () => {
    if (!(await syncOrderIfNeeded(orderInProgress, 'slip'))) return

    // reprint
    dispatch(
      printOrder({
        orderUuid: orderInProgress?.uuid,
        printType: PRINT_TYPES.RECEIPT_AUTH,
        redirectionRoute: '/orders',
      }),
    )
    dispatch(clearPromotionsOnOrder())
    dispatch(clearOrderInProgress())
    dispatch(clearCart())
  }, [syncOrderIfNeeded, orderInProgress, dispatch])

  /**
   * On "Print Receipt", prints the order receipt with conditions for offline cloud printing
   * as well as syncing when necessary
   */
  const printReceipt = useCallback(
    async (isSplitTender = false, isNewOrder = false) => {
      // TODO: refactor to use orderById only
      const curOrder = isSplitTender ? orderInProgress : orderById

      if (!(await syncOrderIfNeeded(curOrder, 'receipt'))) return

      dispatch(
        printOrder({
          orderUuid,
          printType: PRINT_TYPES.RECEIPT,
          isNewOrder,
        }),
      )
    },
    [dispatch, orderById, orderInProgress, orderUuid, syncOrderIfNeeded],
  )

  return {
    printCheckHandler,
    printSlipHandler,
    reprintSlipHandler,
    printReceipt,
  }
}

export default usePrinter
